import type { PriceId } from '@iheartjane/types';
import get from 'lodash-es/get';
import pickBy from 'lodash-es/pickBy';

import type { AlgoliaProduct } from '@jane/search/types';
// eslint-disable-next-line no-restricted-imports
import type { MenuProduct, Product } from '@jane/shared/models';
import type {
  InventoryPotency,
  MaterializedMenuProduct,
  MenuProduct as ZodMenuProduct,
  Product as ZodProduct,
} from '@jane/shared/types';

type ProductType =
  | MenuProduct
  | Product
  | ZodMenuProduct
  | ZodProduct
  | AlgoliaProduct
  | MaterializedMenuProduct;

type PrimaryPotenciesReturn = Partial<{
  percentCbd: number;
  percentTac: number;
  percentThc: number;
  percentThca: number;
}>;

type GetLabResultsReturn = Partial<{
  percentCbd: number;
  percentCbda: number;
  percentTac: number;
  percentThc: number;
  percentThca: number;
}>;

type GetDosageAmountPackInfoReturn = Partial<{
  dosage: string;
  packTotal: string;
  productAmount: string;
}>;

type GetPotencyInfoReturn = Partial<
  ReturnType<typeof getLabResults> & ReturnType<typeof getDosageAmountPackInfo>
> | null;

const nonCannabisKinds = ['gear', 'merch'];

export const formatPotency = (potency?: number | string) =>
  potency ? parseFloat(parseFloat(potency.toString()).toFixed(2)) : null;

export const primaryPotencies = (
  product: ProductType,
  selectedWeight?: PriceId
): PrimaryPotenciesReturn => {
  const primaryPercentCBD: number | undefined = get(
    product,
    'primaryPercentCBD',
    null
  );
  const primaryPercentTHC: number | undefined = get(
    product,
    'primaryPercentTHC',
    null
  );
  const fallbackPercentTAC: number | undefined = get(product, 'percentTAC');
  const fallbackPercentTHCA: number | undefined = get(
    product,
    'percentTHCA',
    null
  );
  const inventoryPotencies: InventoryPotency[] = get(
    product,
    'inventory_potencies',
    []
  );

  const inventoryPotency = inventoryPotencies?.find(
    (potency) => potency.price_id === selectedWeight
  );

  const percentTac =
    inventoryPotency?.tac_potency || fallbackPercentTAC || product.percent_tac;
  const percentThc =
    inventoryPotency?.thc_potency || primaryPercentTHC || product.percent_thc;
  const percentThca =
    inventoryPotency?.thca_potency ||
    fallbackPercentTHCA ||
    product.percent_thca;
  const percentCbd =
    inventoryPotency?.cbd_potency || primaryPercentCBD || product.percent_cbd;

  return pickBy({ percentTac, percentThc, percentThca, percentCbd }, Boolean);
};

export const getPackTotal = (product: ProductType) => {
  let { net_weight_grams = 0, quantity_value = 0 } = product;
  net_weight_grams ??= 0;
  quantity_value ??= 0;

  const shouldAppendPackTotal =
    ['pre-roll', 'vape'].includes(product.kind) &&
    product.root_subtype?.toLowerCase()?.includes('packs');

  if (!shouldAppendPackTotal) {
    return;
  }

  if (product.quantity_units && quantity_value > 0) {
    const standardizedUnits = product.quantity_units.replace('_', ' ');
    return `${quantity_value}${standardizedUnits}`;
  }

  if (net_weight_grams > 0) {
    return `${product.net_weight_grams}g`;
  }

  return;
};

export const getLabResults = (
  product: ProductType,
  selectedWeight?: PriceId
): GetLabResultsReturn => {
  const percentCBDA: number | null = get(product, 'percentCBDA', null);

  const { percentTac, percentThc, percentThca, percentCbd } = primaryPotencies(
    product,
    selectedWeight
  );

  return pickBy(
    {
      percentCbd,
      percentCbda: percentCBDA,
      percentTac,
      percentThc,
      percentThca,
    },
    Boolean
  );
};

export const getDosageAmountPackInfo = (
  product: ProductType
): GetDosageAmountPackInfoReturn => {
  const { amount, dosage } = product;

  if (!dosage && !amount) {
    return {};
  }

  const packTotal = getPackTotal(product);

  return pickBy(
    {
      dosage,
      packTotal,
      productAmount: amount,
    },
    Boolean
  );
};

export const getPotencyInfo = (
  product: ProductType,
  selectedWeight?: PriceId
): GetPotencyInfoReturn => {
  if (nonCannabisKinds.includes(product.kind)) {
    return null;
  }

  const labResults = getLabResults(product, selectedWeight);
  const dosageAmountInfo = getDosageAmountPackInfo(product);

  return { ...dosageAmountInfo, ...labResults };
};
